import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { ref, watch, unref, withDefaults } from 'vue';
import * as monacoEditorHelper from '@/monaco-editor-helper';

export interface PropsOptions {
	hidden: boolean,
	readOnly: boolean,
	fontSize: number,
	wordWrap: boolean,
	content: string,
	modelLanguage: string,
	followVirtualCursor: boolean,
	virtualCursorPosition: CursorPosition|null,
	virtualSelections: Selections,
}

export interface EditorState {
	cursorPosition?: CursorPosition|null,
	selections?: Selections,
	viewState?: monaco.editor.ICodeEditorViewState|null,
}

type Decorations = {
	virtualCursor: Array<monaco.editor.IModelDeltaDecoration>,
	virtualSelections: Array<monaco.editor.IModelDeltaDecoration>,
}

type IgnoredEventsCounterEventsIgnorationDoneCallbackFunction = () => void;
type IgnoredEventsCounterIgnoreCallbackFunction = (eventsIgnorationDone:IgnoredEventsCounterEventsIgnorationDoneCallbackFunction) => Promise<void>;


import * as monaco from 'monaco-editor';

export interface Tab {
	filename: string,
	name: string,
	active: boolean,
	isLectorActiveEditor: boolean,
}

export const EmitsOptions = {
	'onMouseDown' : (e: monaco.editor.IEditorMouseEvent) => {return true;},
	'onKeyDown' : (e: monaco.IKeyboardEvent) => {return true;},
	'onDidScrollChange' : (e: monaco.IScrollEvent) => {return true;},
}

export type CursorPosition = {
	lineNumber: number,
	column: number,
}

export type Selections = Array<{
	start: {
		lineNumber: number,
		column: number,
	},
	end: {
		lineNumber: number,
		column: number,
	},
}>;

export default /*#__PURE__*/_defineComponent({
  __name: 'IDETextEditor',
  props: {
    hidden: { type: Boolean },
    readOnly: { type: Boolean, default() {
		return false;
	} },
    fontSize: { default() {
		return 18;
	} },
    wordWrap: { type: Boolean, default() {
		return false;
	} },
    content: { default() {
		return "";
	} },
    modelLanguage: { default() {
		return "";
	} },
    followVirtualCursor: { type: Boolean, default() {
		return false;
	} },
    virtualCursorPosition: { default() {
		return null;
	} },
    virtualSelections: { default() {
		return [];
	} }
  },
  emits: EmitsOptions,
  setup(__props: any, { expose, emit }) {

const props = __props as { hidden: boolean, readOnly: boolean, fontSize: number, wordWrap: boolean, content: string, modelLanguage: string, followVirtualCursor: boolean, virtualCursorPosition: CursorPosition|null, virtualSelections: Selections }






// definice method, ktere mohou volat jine komponenty z venku
expose({
	getEditorState() : EditorState|null {
		//console.warn('getEditorState');
		if (!monacoEditor) {
			return null;
		}

		return {
			cursorPosition: monacoEditor.getPosition(),
			selections: (monacoEditor.getSelections() || []).map((selection) => {
				return {
					start: {
						lineNumber: selection.selectionStartLineNumber,
						column: selection.selectionStartColumn,
					},
					end: {
						lineNumber: selection.positionLineNumber,
						column: selection.positionColumn,
					},
				};
			}),
			viewState: monacoEditor.saveViewState(),
		};
	},
	setEditorState(editorState:EditorState) {
		//console.warn('setEditorState');
		if (!monacoEditor) {
			return;
		}

		if (editorState.cursorPosition) {
			setEditorCursorPosition(editorState.cursorPosition);
		}
		if (editorState.selections) {
			setEditorSelections(editorState.selections);
		}
		if (editorState.viewState) {
			setEditorViewState(editorState.viewState);
		}
	},
	focus() {
		if (!monacoEditor) {
			return;
		}

		monacoEditor.focus();
	},
	setContent(content:string) {
		setEditorContent(content);
	},
});

const elIDETextEditor = ref<HTMLElement|null>(null);
// pomocny timer, abychom neprovadeli schovavani a zobrazovani textoveho editoru
// neustale pri zmene velikosti
let resizeTimer:number|null = null;
let ignoreResizeTime:number|null = null;
function resizeEditor() {
	//console.log('resizeEditor');
	if (ignoreResizeTime !== null && ignoreResizeTime > Date.now()) {
		//console.log('ignoreResizeTime');
		return;
	}

	if (!monacoEditor || !elIDETextEditor.value || !elMonacoEditorContainer.value) {
		return;
	}

	// pokud uz zmena neprobiha...
	if (resizeTimer !== null) {
		// abychom zjistili spravnou velikost, aby byl editor roztazeny na obsah
		// sveho kontaineru, ale zbytecne kontainer nezvetsoval, tak nejdrive
		// kontainer schovame, zjistime velikost hlavniho elementu a podle toho
		// nastavime velikost kontaineru
		const elContainer = elMonacoEditorContainer.value;
		// shovat kontainer
		elContainer.style.display = 'none';
		//console.log('display none');
		clearTimeout(resizeTimer);
		resizeTimer = null;
	}
	// pockame nejaky cas se shovanym containerem a az pak tam vratime editor
	//console.log('start timer');
	resizeTimer = setTimeout(() => {
		//console.log('timer ticked');
		resizeTimer = null;
		if (!monacoEditor || !elIDETextEditor.value || !elMonacoEditorContainer.value) {
			return;
		}

		// po nejakou dobu nereagovat na dalsi zmenu velikost, aby se nam to nahodou nezacyklilo
		//ignoreResizeTime = Date.now() + 400;

		// zobrazit editor a nechat se prizpusobit kontaineru
		relayoutEditor();
	}, 250);
}
// hlidat zmenu velikosti hlavniho elementu a podle toho nastavovat velikost
// editoru
watch(elIDETextEditor, () => {
	if (!elIDETextEditor.value) {
		return;
	}
	const resizeObserver = new ResizeObserver((entries) => {
		// for (const entry of entries) {
		// 	console.log({entry});
		// }
		//console.log('elIDETextEditor resized');
		resizeEditor();
	});
	resizeObserver.observe(elIDETextEditor.value);
});

const elMonacoEditorContainer = ref<HTMLElement|null>(null);
let monacoEditor:monaco.editor.IStandaloneCodeEditor|null = null;

class IgnoredEventsCounter {
	static id = 0;
	counters:Set<number> = new Set();

	async ignore(cb:IgnoredEventsCounterIgnoreCallbackFunction) {
		const id = IgnoredEventsCounter.id++;
		this.counters.add(id);

		await cb(() => {
			this.counters.delete(id);
		});
	}

	shouldIgnoreEvents() {
		return this.counters.size > 0;
	}
}
const ignoredEventsCounter = new IgnoredEventsCounter();

watch(elMonacoEditorContainer, () => {
	if (elMonacoEditorContainer.value) {
		monaco.editor.defineTheme('PF', monacoEditorHelper.themeData);
		
		monacoEditor = monaco.editor.create(elMonacoEditorContainer.value, {
			value: props.content,
			language: props.modelLanguage,
			readOnly: props.readOnly,
			automaticLayout: false,
			theme: "PF",
			wordWrap: props.wordWrap ? "on" : "off",
			renderValidationDecorations: "off",
			fontSize: props.fontSize,
			scrollBeyondLastLine: false,
			roundedSelection: false,
			quickSuggestions: false,
			colorDecorators: false,
			contextmenu: true,
			smoothScrolling: true,
			hover: {
				enabled: false,
			},
			emptySelectionClipboard: false,
			codeLens: false,
			folding: false,
			bracketPairColorization: {
				enabled: true,
				independentColorPoolPerBracketType: true,
			},
		});
		//window.monacoEditor = monacoEditor;
		monacoEditor.onMouseDown((e:monaco.editor.IEditorMouseEvent) => {
			emit('onMouseDown', e);
		});
		monacoEditor.onKeyDown((e:monaco.IKeyboardEvent) => {
			emit('onKeyDown', e);
		});
		monacoEditor.onDidScrollChange((e:monaco.IScrollEvent) => {
			if ((e.scrollLeftChanged || e.scrollTopChanged) && !ignoredEventsCounter.shouldIgnoreEvents()) {
				//console.log('onDidScrollChange', e);
				//debugger
				emit('onDidScrollChange', e);
			}
			if ((e.scrollWidthChanged || e.scrollHeightChanged)) {
				scrollEditorToCursorPosition();
			}
		});

		resizeEditor();
		applyDecorations();
	}else {
		monacoEditor = null;
	}
});


watch(() => props.content, () => {
	if (monacoEditor && props.content !== undefined) {
		setEditorContent(props.content);
	}
});

watch(() => props.fontSize, () => {
	if (monacoEditor) {
		monacoEditor.updateOptions({
			fontSize: props.fontSize,
		});
	}
});

watch(() => props.wordWrap, () => {
	if (monacoEditor) {
		monacoEditor.updateOptions({
			wordWrap: props.wordWrap ? 'on' : 'off',
		});
	}
});

function relayoutEditor() {
	//console.warn("relayoutEditor");
	// nastavit ignorovani udalosti, aby se nevyvolali pri zmene layoutu
	ignoredEventsCounter.ignore(async (eventsIgnorationDone) => {
		if (!monacoEditor || !elIDETextEditor.value || !elMonacoEditorContainer.value || props.hidden) {
			//console.log('no editor or hidden');
			eventsIgnorationDone();
			return;
		}

		// zjistit jak je velky hlavni element
		const rect = elIDETextEditor.value.getBoundingClientRect();
		//console.log({width: rect.width, height: rect.height});
		// nastavit velikost kontaineru podle hlavniho elementu
		const elContainer = elMonacoEditorContainer.value;
		elContainer.style.display = 'block';
		//console.log('display block');
		elContainer.style.width = `${rect.width}px`;
		//console.log("layout", rect.height);

		monacoEditor.layout({width: rect.width, height: rect.height});
		
		// prestat ignorovat udalosti
		setTimeout(() => {
			eventsIgnorationDone();
		}, 1000)
	});
}

function setEditorContent(newContent:string) {
	//console.warn("setEditorContent", newContent);
	// nastavit ignorovani udalosti, aby se nevyvolali pri zmene obsahu
	ignoredEventsCounter.ignore(async (eventsIgnorationDone) => {
		if (monacoEditor) {
			monacoEditorHelper.setEditorContent(monacoEditor, newContent);

			applyDecorations();

			// nascrolovat tak, aby byla videt pozice kursoru
			scrollEditorToCursorPosition();
		}
		
		// prestat ignorovat udalosti
		eventsIgnorationDone();
	});
}

async function scrollEditorToCursorPosition() {
	//console.warn("scrollEditorToCursorPosition");
	// nastavit ignorovani udalosti, aby se nevyvolali pri zmene scroll pozice
	ignoredEventsCounter.ignore(async (eventsIgnorationDone) => {
		let scrollPositionChanged = false;

		if (monacoEditor) {
			const cursorPosition = props.followVirtualCursor ? props.virtualCursorPosition : null/*monacoEditor.getPosition()*/;
			if (cursorPosition) {
				for (let loop = 1; loop <= 2; loop++) {
					// zjistime pozici kursoru
					const cursor = {
						top: monacoEditor.getTopForPosition(cursorPosition.lineNumber, cursorPosition.column),
						left: monacoEditor.getOffsetForColumn(cursorPosition.lineNumber, cursorPosition.column),
						height: monacoEditor.getOption(monaco.editor.EditorOption.lineHeight),
						width: monacoEditor.getOffsetForColumn(cursorPosition.lineNumber, 2) - monacoEditor.getOffsetForColumn(cursorPosition.lineNumber, 1),
					};
					//console.log('cursor', JSON.stringify(cursor));

					// zjistime velikost viewportu
					const layoutInfo = monacoEditor.getLayoutInfo();
					const viewport = {
						width: layoutInfo.contentWidth,
						height: layoutInfo.height,
					};
					//console.log('viewport', JSON.stringify(viewport));

					// zjistime velikost obsahu
					const content = {
						width: monacoEditor.getContentWidth(),
						height: monacoEditor.getContentHeight(),
					};
					//console.log('content', JSON.stringify(content));

					// zjistime aktualni scroll pozici
					const scroll = {
						top: monacoEditor.getScrollTop(),
						left: monacoEditor.getScrollLeft(),
						maxTop: Math.max(content.height - viewport.height, 0),
						maxLeft: Math.max(content.width - viewport.width, 0),
					};
					//console.log('scroll', JSON.stringify(scroll));

					// zjistime zda-li je potreba opravdu nascrolovat
					// aby to nonstop nescolovalo, tak chceme scrolovat jen kdyz
					// je pozice mimo viewport nebo na kraji viewportu
					// TODO - treba nekdy priste :-)

					// spocitame scroll pozici, tak aby cursor byl
					// vertikalne uprostred viewportu
					// a horizontalne vzdy aby byl kurzor nekolik znaku od praveho okraje
					const scrollPosition = {
						top: Math.max(
								Math.min(
									cursor.top + (cursor.height / 2) - (viewport.height / 2),
									scroll.maxTop
								),
								0
							),
						left: cursor.left == -1 ? -1 : Math.max(
							Math.min(
								cursor.left + (5/*kolik znaku zprava*/)*cursor.width - viewport.width,
								scroll.maxLeft
							),
							0
						),
					};

					//console.log(JSON.stringify(scrollPosition));
					if (scroll.top != scrollPosition.top) {
						monacoEditor.setScrollTop(scrollPosition.top, monaco.editor.ScrollType.Immediate);
						//console.log('setScrollTop', scrollPosition.top);
						scrollPositionChanged = true;
					}
					if (scrollPosition.left != -1) {
						if (scroll.left != scrollPosition.left) {
							//console.log('setScrollLeft', scrollPosition.left);
							monacoEditor.setScrollLeft(scrollPosition.left, monaco.editor.ScrollType.Immediate);
							scrollPositionChanged = true;
						}
						break;
					}
				}
			}
		}

		// prestat ignorovat udalosti
		//console.log({scrollPositionChanged});
		if (scrollPositionChanged) {
			setTimeout(() => {
				eventsIgnorationDone();
			}, 250);
		}else {
			eventsIgnorationDone();
		}
	});
}
//window.scrollEditorToCursorPosition = scrollEditorToCursorPosition;
function setEditorCursorPosition(cursorPosition: CursorPosition) {
	//console.warn('setEditorCursorPosition', {cursorPosition});
	// nastavit ignorovani udalosti, aby se nevyvolali pri zmene obsahu
	ignoredEventsCounter.ignore(async (eventsIgnorationDone) => {
		if (monacoEditor) {
			monacoEditor.setPosition({
				lineNumber: cursorPosition.lineNumber,
				column: cursorPosition.column,
			});

			scrollEditorToCursorPosition();
		}

		// prestat ignorovat udalosti
		eventsIgnorationDone();
	});
}

function setEditorSelections(selections: Selections) {
	//console.warn('setEditorSelections');
	// nastavit ignorovani udalosti, aby se nevyvolali pri zmene obsahu
	ignoredEventsCounter.ignore(async (eventsIgnorationDone) => {
		if (monacoEditor) {
			monacoEditor.setSelections(selections.map((selection) => {
				return {
					selectionStartLineNumber: selection.start.lineNumber,
					selectionStartColumn: selection.start.column,
					positionLineNumber: selection.end.lineNumber,
					positionColumn: selection.end.column,
				};
			}));
		}

		// prestat ignorovat udalosti
		eventsIgnorationDone();
	});
}

function setEditorViewState(viewState: monaco.editor.ICodeEditorViewState) {
	//console.warn("setEditorViewState");
	// nastavit ignorovani udalosti, aby se nevyvolali pri zmene obsahu
	ignoredEventsCounter.ignore(async (eventsIgnorationDone) => {
		if (monacoEditor) {
			monacoEditor.restoreViewState(viewState);
		}

		// pockat nejaky cas nez se tam editor doscroluje a pak az prestat ignorovat udalosti
		setTimeout(() => {
			eventsIgnorationDone();
		}, 2000);
	});
}

let monacoDecorationIds:string[] = [];
const monacoDecorations:Decorations = {
	virtualCursor: [],
	virtualSelections: [],
};

watch(() => props.virtualCursorPosition, () => {
	//console.warn("virtualCursorPosition changed");
	if (monacoEditor && props.virtualCursorPosition) {
		if (props.followVirtualCursor === true) {
			setEditorCursorPosition({
				lineNumber: props.virtualCursorPosition.lineNumber,
				column: /*props.virtualCursorPosition.column*/ 1 /*dat kurzor na zacatek at se tam neprebiji s dekoraci virtualniho kurzoru*/,
			});

			// nascrolovat tak, aby byla videt pozice kursoru
			scrollEditorToCursorPosition();
		}
	}

	const virtualCursorDecorations:monaco.editor.IModelDeltaDecoration[] = [];
	if (props.virtualCursorPosition) {
		virtualCursorDecorations.push({
			range: new monaco.Range(
				props.virtualCursorPosition.lineNumber,
				props.virtualCursorPosition.column,
				props.virtualCursorPosition.lineNumber,
				props.virtualCursorPosition.column + 1,
			), 
			options: {
				className: 'virtualCursor',
			},
		});

		virtualCursorDecorations.push({
			range: new monaco.Range(
				props.virtualCursorPosition.lineNumber,
				props.virtualCursorPosition.column,
				props.virtualCursorPosition.lineNumber,
				props.virtualCursorPosition.column,
			), 
			options: {
				className: 'virtualCursorLine',
				isWholeLine: true,
				marginClassName: 'virtualCursorLineMargin',
			},
		});
	}
	monacoDecorations.virtualCursor = virtualCursorDecorations;
	applyDecorations();
});

watch(() => props.virtualSelections, () => {
	const virtualSelectionsDecorations:monaco.editor.IModelDeltaDecoration[] = [];
	if (props.virtualSelections !== undefined) {
		for (const virtualSelection of props.virtualSelections) {
			if (virtualSelection.start.column !== virtualSelection.end.column || virtualSelection.start.lineNumber !== virtualSelection.end.lineNumber) {
				virtualSelectionsDecorations.push({
					range: new monaco.Range(
						virtualSelection.start.lineNumber,
						virtualSelection.start.column,
						virtualSelection.end.lineNumber,
						virtualSelection.end.column,
					),
					options: {
						inlineClassName: 'virtualSelection',
						beforeContentClassName: 'virtualSelectionBefore',
						afterContentClassName: 'virtualSelectionAfter',
						zIndex: -1,
					},
				});
			}
		}
	}
	monacoDecorations.virtualSelections = virtualSelectionsDecorations;
	applyDecorations();
}, {deep: true});

function applyDecorations() {
	if (monacoEditor) {
		const model = monacoEditor.getModel();

		if (model) {
			const decorations:monaco.editor.IModelDeltaDecoration[] = Object.values(unref(monacoDecorations)).flat();

			monacoDecorationIds = model.deltaDecorations(monacoDecorationIds, decorations);
		}
	}
}

watch(() => props.modelLanguage, () => {
	if (monacoEditor && props.modelLanguage !== undefined) {
		const model = monacoEditor.getModel();
		if (model) {
			/// nastavit ignorovani udalosti, aby se nevyvolali pri zmene jazyka
			ignoredEventsCounter.ignore(async (eventsIgnorationDone) => {
				monaco.editor.setModelLanguage(model, props.modelLanguage);

				// prestat ignorovat udalosti
				setTimeout(() => {
					eventsIgnorationDone();
				}, 1000)
			});
		}
	}
});

// hlidat velikost okna a podle toho upozornovat editor na to,
// aby prizpusobil svoji velikost
watch(elMonacoEditorContainer, () => {
	if (!elMonacoEditorContainer.value) {
		return;
	}
	const resizeObserver = new ResizeObserver((entries) => {
		// for (const entry of entries) {
		// 	console.log({entry});
		// }
		//console.log('Window resized');
		resizeEditor();
	});
	let node:Element|null = elMonacoEditorContainer.value;
	while (node) {
		resizeObserver.observe(node);
		node = node.parentNode ? node.parentElement : null;
	}
});
watch(() => props.hidden, () => {
	//console.log('hidden changed');
	resizeEditor();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["IDETextEditor", {hidden: __props.hidden}]),
    ref_key: "elIDETextEditor",
    ref: elIDETextEditor
  }, [
    _createElementVNode("div", {
      class: "monaco-editor-container",
      ref_key: "elMonacoEditorContainer",
      ref: elMonacoEditorContainer
    }, null, 512)
  ], 2))
}
}

})